import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { fetchMissingDiscs } from '../lib/api';
import { MISSING_DISCS_COUNT_EVENT, missingLists } from '../lib/missingDiscs';
import { isEmbedded } from '../lib/embedTokenHandoff';
import { workspaceSections, sectionItems, isCurrent, currentSection } from '../lib/workspaceNav';
import OrgSwitcher from './OrgSwitcher';
import WorkspaceMenu from './WorkspaceMenu';
import { version as PKG_VERSION } from '../../package.json';

// The deployed truth: CI injects the git tag as VITE_APP_VERSION at
// image build (Dockerfile ARG APP_VERSION). package.json is only the
// dev-server fallback — it is NOT bumped per release, which is how the
// sidebar sat on v3.34.3 while prod ran v3.37.0.
const APP_VERSION = (import.meta.env.VITE_APP_VERSION || PKG_VERSION).replace(/^v/, '');
import {
  Gauge,
  Sun,
  CalendarDays,
  Users,
  AlertTriangle,
  Route,
  ShieldAlert,
  Award,
  Database,
  Radar,
  FileBarChart,
  FileText,
  PanelLeftClose,
  PanelLeft,
  Eye,
  Moon,
  Shield,
  Settings,
  UserCog,
  LogOut,
  User,
  Disc3,
  Menu,
  X,
} from 'lucide-react';

/* One icon per destination. The map itself (names, order, groups and the
   role gates) is lib/workspaceNav, shared with the embedded top bar. */
const ICONS = {
  today: Sun,
  calendar: CalendarDays,
  people: Users,
  infringements: AlertTriangle,
  mileage: Route,
  fraudAlerts: ShieldAlert,
  speeding: Gauge,
  earnedRecognition: Award,
  dataCentre: Database,
  discoveries: Radar,
  analogue: Disc3,
  managementReport: FileBarChart,
  reportSuite: FileText,
  orgSettings: Settings,
  userManagement: UserCog,
  superAdmin: Shield,
};

export default function Sidebar({ isExpanded: expandedSetting, activeView, onNavigate, embedded = false, dashboardNavigation = { tab: 'today', records: [] } }) {
  const { user, isAdmin, isSuperAdmin, analogueEnabled, analogueHasCharts, logout } = useAuth();
  /* The phone sheet (standalone, narrow screens): always drawn expanded. */
  const [sheet, setSheet] = useState(false);
  const isExpanded = expandedSetting || sheet;
  /* Seeded from the class the pre-paint script in index.html already applied,
     NOT from a fresh storage read. Two readers of one setting drift, and the
     class is the thing the user is actually looking at. */
  const [theme, setTheme] = useState(() => {
    if (typeof document === 'undefined') return 'dark';
    const r = document.documentElement.classList;
    if (r.contains('high-contrast')) return 'high-contrast';
    if (r.contains('light')) return 'light';
    return 'dark';
  });
  useEffect(() => {
    const root = document.documentElement;
    const update = () => setTheme(root.classList.contains('light') ? 'light' : root.classList.contains('high-contrast') ? 'high-contrast' : 'dark');
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  /* Dark -> Light -> High Contrast -> Dark. The control is labelled with the
     NEXT mode, so the label is a promise rather than a status (theme spec v1,
     Appendix A). */
  /* GATE LIFTED 2026-08-31 (v3.53.9), at Zed's explicit instruction, so the
     toggle can be used to evaluate the theme.

     History worth keeping: light shipped in v3.53.5 on green contrast tests
     and came back "washed out, buttons not clear". The inks were never the
     fault — every one clears AA on both light grounds — but 217 inline
     rgba(15,23,42,0.x) SURFACE fills were still dark-theme slabs, so correct
     text sat on grey. v3.53.6 re-gated; v3.53.7 tokenised the surfaces via
     the --surface-rgb / --border-rgb / --mark-rgb triplets; v3.53.8 fixed
     control boundaries (--control-border), a white button's edge having been
     1.53:1 against its own fill.

     The standing lesson, unchanged by this lift: a contrast suite cannot see
     a design-model fault. It was green for the version that shipped broken.
     If this theme is judged bad again, the evidence will be someone looking
     at it, not a number.

     Flip this map and the assertion in themePersistence.test.mjs together. */
  const THEME_CYCLE = { dark: 'light', light: 'high-contrast', 'high-contrast': 'dark' };
  const THEME_LABEL = { dark: 'Dark mode', light: 'Light mode', 'high-contrast': 'High contrast' };

  const cycleTheme = () => {
    setTheme((prev) => {
      const next = THEME_CYCLE[prev] || 'dark';
      const r = document.documentElement.classList;
      /* The class combinations are NOT symmetric (spec s2.1): dark stays on for
         high contrast because Tailwind dark: variants assume it, and dark must
         come OFF for light so those variants stop applying. Kept identical to
         the pre-paint script in index.html - if these two ever disagree the
         theme flips on the first render after load. */
      r.toggle('dark', next !== 'light');
      r.toggle('high-contrast', next === 'high-contrast');
      r.toggle('light', next === 'light');
      /* PERSIST. Before this, high contrast was React state only and was lost
         on every reload - a user who needs it had to re-enable it each time.
         Key and values are DDIR's shared vocabulary (theme spec v1 s2.2), so
         this already speaks the right language when the cross-product cookie
         lands. Wrapped because localStorage THROWS in private mode; failing to
         remember is a poor outcome, but taking the sidebar down with it is a
         worse one. */
      try {
        localStorage.setItem('ddir-theme', next);
      } catch (e) { /* storage unavailable - the class still applied */ }
      return next;
    });
  };

  /* The Missing discs section's count on the Analogue charts entry (design
     2026-10-06 §6): the gaps, held gaps and readings to put right, the
     likely-yard gaps left out (lib missingLists `badge`). Asked for once the
     entry is shown, for a manager (the door is require_admin), and moved on
     by the section whenever its own list loads (MISSING_DISCS_COUNT_EVENT),
     so a gap closed on the page leaves the count at once. A failed ask shows
     no number: no number is not "none", and a made-up one is worse. */
  const showAnalogue = Boolean(isAdmin && (analogueEnabled || analogueHasCharts));
  const onAnalogue = activeView === 'analogue';
  const [missingCount, setMissingCount] = useState(0);
  /* One counter orders the sidebar's own requests and the section's
     announcements (review M5): a response is used only when no announcement
     has come since its request went out, so a slow answer from before a
     close never puts the old count back. */
  const order = useRef({ seq: 0, announced: 0 });

  /* The section's announcements, heard whenever the entry is shown. */
  useEffect(() => {
    if (!showAnalogue) {
      setMissingCount(0);
      return undefined;
    }
    const heard = (e) => {
      order.current.announced = ++order.current.seq;
      const n = e && typeof e.detail === 'number' ? e.detail : 0;
      setMissingCount(n > 0 ? n : 0);
    };
    window.addEventListener(MISSING_DISCS_COUNT_EVENT, heard);
    return () => window.removeEventListener(MISSING_DISCS_COUNT_EVENT, heard);
  }, [showAnalogue]);

  /* The sidebar's own ask, with ?persist=0 (the chain walked in memory, no
     lock, no write), so the count costs nothing to keep. Not while the
     Analogue charts page is open: its section reads the list itself and
     announces the count. */
  useEffect(() => {
    if (!showAnalogue || onAnalogue) return undefined;
    let live = true;
    const asked = ++order.current.seq;
    const fresh = () => live && order.current.announced < asked;
    fetchMissingDiscs({ persist: false })
      .then((body) => { if (fresh()) setMissingCount(missingLists(body).badge); })
      .catch(() => { if (fresh()) setMissingCount(0); });
    return () => { live = false; };
  }, [showAnalogue, onAnalogue, user?.org_id]);

  /* ── The one map (lib/workspaceNav), drawn below as a sidebar or a top
     bar. The Analogue charts entry is a COURTESY, not the access control:
     the upload endpoint checks the same entitlement itself, because a
     hidden menu item protects nothing on the open internet. It is off
     whenever the lookup failed, so an org that cannot be asked is never
     handed a screen that 403s the moment they use it. Only for an org that
     has the chart reader, or charts on file (design 2026-10-06). */
  let analogue = false;
  if (analogueEnabled || analogueHasCharts) {
    analogue = true;
  }
  const activeTab = dashboardNavigation?.tab || 'today';
  const sections = workspaceSections({
    recordTabs: (dashboardNavigation?.records || []).map((r) => r.key),
    isAdmin,
    isSuperAdmin,
    analogue,
  });
  const fraudCount = Number(dashboardNavigation?.fraudCount) || 0;
  const countFor = (item) => {
    if (item.key === 'analogue' && missingCount > 0) return missingCount;
    if (item.key === 'fraudAlerts' && fraudCount > 0) return fraudCount;
    return 0;
  };
  const countWordsFor = (item, count) => {
    if (!count) return '';
    if (item.key === 'analogue') return `${count} possible missing disc${count === 1 ? '' : 's'}`;
    return `${count} to explain`;
  };
  const go = (item) => {
    setSheet(false); // Also close when the current destination is selected.
    onNavigate(item.view, item.tab);
  };
  const onScreen = currentSection(sections, activeView, activeTab);
  const pageLabel = sectionItems(onScreen).find((i) => isCurrent(i, activeView, activeTab))?.label || 'Tachograph';

  /* One destination as a button: the count's accessible name is the
     button's (review M4), "Analogue charts, 3 possible missing discs", the
     visual badge itself hidden from assistive technology. */
  const navButton = (item, { className = 'sidebar-item', withIcon = true } = {}) => {
    const Icon = ICONS[item.key];
    const active = isCurrent(item, activeView, activeTab);
    const count = countFor(item);
    const countWords = countWordsFor(item, count);
    return (
      <button
        type="button"
        key={item.key}
        className={`${className}${active ? ` ${className}--active` : ''}`}
        onClick={() => go(item)}
        title={!isExpanded && !embedded ? [item.label, countWords].filter(Boolean).join(': ') : countWords || undefined}
        aria-label={count ? `${item.label}, ${countWords}` : item.label}
        aria-current={active ? 'page' : undefined}
      >
        {withIcon && Icon && <span className="sidebar-icon"><Icon size={17} /></span>}
        {(isExpanded || !withIcon) && <span className="sidebar-label">{item.label}</span>}
        {count > 0 && (
          <span className="nav-count" aria-hidden="true">{count}</span>
        )}
      </button>
    );
  };

  /* Theme cycle — Dark, Light, High contrast. Labelled with the NEXT mode:
     the label is a promise, not a status. One control, drawn in the
     standalone account row and in the embedded top bar. */
  const themeButton = (
    <button
      type="button"
      className="sidebar-tool"
      onClick={cycleTheme}
      title={`Switch to ${THEME_LABEL[THEME_CYCLE[theme]]}`}
      aria-label={`Switch to ${THEME_LABEL[THEME_CYCLE[theme]]}`}
    >
      {theme === 'dark' ? <Sun size={16} /> : theme === 'light' ? <Eye size={16} /> : <Moon size={16} />}
    </button>
  );

  if (embedded) {
    return (
      <nav className="embedded-nav" aria-label="Tachograph sections">
        <div className="embedded-nav-primary">
          {sections.map((section) => {
            if (section.target) return navButton(section.target, { className: 'embedded-nav-link', withIcon: false });
            const items = sectionItems(section);
            const total = items.reduce((n, item) => n + countFor(item), 0);
            return (
              <WorkspaceMenu key={section.key} label={section.label} active={onScreen === section} count={total}>
                {section.groups
                  ? section.groups.map((group) => (
                    <div key={group.key} className="workspace-menu-group" role="group" aria-label={group.label}>
                      <div className="workspace-menu-heading" aria-hidden="true">{group.label}</div>
                      {group.items.map((item) => navButton(item, { className: 'embedded-nav-link', withIcon: false }))}
                    </div>
                  ))
                  : items.map((item) => navButton(item, { className: 'embedded-nav-link', withIcon: false }))}
              </WorkspaceMenu>
            );
          })}
        </div>
        <div className="embedded-nav-utilities">
          <OrgSwitcher isExpanded />
          {/* DDIR sends no theme message, so the embed keeps its own cycle
              (the base sidebar had it in both shells). */}
          {themeButton}
        </div>
      </nav>
    );
  }

  return (
    <StandaloneShell isExpanded={isExpanded} pageLabel={pageLabel} activeView={activeView} activeTab={activeTab} sheet={sheet} setSheet={setSheet}>
      {({ closeSheet }) => (
        <>
          {/* ── Logo Area ── */}
          <div className="sidebar-logo-area">
            <div className="sidebar-logo-ring">
              <Gauge size={18} style={{ color: 'var(--accent)' }} />
            </div>
            {isExpanded && <span className="sidebar-org-name">TachoParser</span>}
            <button type="button" className="sidebar-sheet-close" onClick={closeSheet} aria-label="Close menu">
              <X size={18} />
            </button>
          </div>

          {/* ── Organisation Switcher ── */}
          <OrgSwitcher isExpanded={isExpanded} />

          {/* ── Navigation: the front door, then Records, Reports, Settings ── */}
          <nav className="sidebar-nav" aria-label="TachoParser navigation">
            <div className="sidebar-section">
              {sections.filter((s) => s.target).map((s) => navButton(s.target))}
            </div>
            {sections.filter((s) => !s.target).map((section) => (
              <div key={section.key} className="sidebar-section">
                {isExpanded ? <div className="sidebar-section-label">{section.label}</div> : <div className="sidebar-section-rule" />}
                {section.groups
                  ? section.groups.map((group) => (
                    <div key={group.key} className="sidebar-group" role="group" aria-label={group.label}>
                      {isExpanded && section.groups.length > 1 && <div className="sidebar-group-label" aria-hidden="true">{group.label}</div>}
                      {group.items.map((item) => navButton(item))}
                    </div>
                  ))
                  : sectionItems(section).map((item) => navButton(item))}
              </div>
            ))}
          </nav>

          {/* ── Account: who is signed in, then one row of quiet tools ── */}
          <div className="sidebar-bottom">
            {user && isExpanded && (
              <div className="sidebar-account" title={user.email}>
                <User size={15} aria-hidden="true" />
                <span>{user.display_name || user.email}</span>
              </div>
            )}
            <div className="sidebar-tools">
              {themeButton}
              {/* Sign out — hidden inside the DDIR iframe (v3.50.4): the embed
                  session is DDIR-minted, so a manual sign-out there makes no
                  sense. Only the CONTROL is gated — AuthContext's logout /
                  session-expiry paths still run in the embed. */}
              {!isEmbedded() && (
                <button type="button" className="sidebar-tool" onClick={logout} title="Sign out" aria-label="Sign out">
                  <LogOut size={16} />
                </button>
              )}
              {/* Collapse / expand (desktop only; the phone sheet is full width) */}
              <button
                type="button"
                className="sidebar-tool sidebar-collapse"
                onClick={() => onNavigate('__toggle_sidebar__')}
                title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {isExpanded ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
              </button>
            </div>
          </div>

          {/* ── Footer ── */}
          {isExpanded && <div className="sidebar-footer">TachoParser v{APP_VERSION}</div>}
        </>
      )}
    </StandaloneShell>
  );
}

/* The standalone frame. On a laptop it is the sidebar; on a phone the
   sidebar would eat a sixth of the screen, so it becomes a top bar with the
   page's name and a Menu button that opens the same sidebar as a sheet. A
   destination pressed in the sheet closes it (the page changed). */
function StandaloneShell({ isExpanded, pageLabel, activeView, activeTab, sheet, setSheet, children }) {
  const panel = useRef(null);
  const menuButton = useRef(null);
  const closeSheet = () => setSheet(false);
  useEffect(() => { setSheet(false); }, [activeView, activeTab, setSheet]);
  useEffect(() => {
    if (!sheet) return undefined;
    const menu = panel.current;
    const trigger = menuButton.current;
    const desktop = window.matchMedia('(min-width: 761px)');
    const closeOnDesktop = () => { if (desktop.matches) setSheet(false); };
    const focusable = () => [...menu.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]')]
      .filter((node) => node.getClientRects().length > 0);
    focusable()[0]?.focus();
    const keys = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setSheet(false);
      }
      if (e.key === 'Tab') {
        const items = focusable();
        const first = items[0];
        const last = items.at(-1);
        if (e.shiftKey && (document.activeElement === first || !menu.contains(document.activeElement))) {
          e.preventDefault(); last?.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !menu.contains(document.activeElement))) {
          e.preventDefault(); first?.focus();
        }
      }
    };
    document.addEventListener('keydown', keys);
    desktop.addEventListener('change', closeOnDesktop);
    closeOnDesktop();
    return () => {
      document.removeEventListener('keydown', keys);
      desktop.removeEventListener('change', closeOnDesktop);
      if (trigger?.getClientRects().length) trigger.focus();
    };
  }, [sheet, setSheet]);
  return (
    <>
      <header className="phone-bar">
        <span className="phone-bar-brand"><Gauge size={16} style={{ color: 'var(--accent)' }} /> {pageLabel}</span>
        <button type="button" ref={menuButton} className="btn btn--ghost btn--sm" onClick={() => setSheet(true)} aria-expanded={sheet} aria-controls="tacho-sidebar">
          <Menu size={16} /> <span>Menu</span>
        </button>
      </header>
      {sheet && <div className="sidebar-scrim" onClick={closeSheet} aria-hidden="true" />}
      <aside
        id="tacho-sidebar"
        ref={panel}
        role={sheet ? 'dialog' : undefined}
        aria-modal={sheet ? true : undefined}
        className={`sidebar-shell ${isExpanded ? '' : 'sidebar-shell--collapsed'} ${sheet ? 'sidebar-shell--sheet' : ''}`}
        style={{ width: isExpanded ? 248 : 56 }}
        aria-label="TachoParser navigation"
      >
        {children({ closeSheet })}
      </aside>
    </>
  );
}
